import * as React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/taskpane/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/taskpane/components/ui/tabs";
import { Alert, AlertDescription } from "@/taskpane/components/ui/alert";
import { Button } from "@/taskpane/components/ui/button";
import { Input } from "@/taskpane/components/ui/input";
import { Field, FieldContent } from "@/taskpane/components/ui/field";
import { useUser } from "@/hooks/useUser";
import { referUser } from "@/services/user.service";
import { useClipboard } from "@/hooks/useClipboard";
import { toast } from "sonner";
import {
    CircleDollarSign,
    Star,
    Copy,
    Twitter,
    Linkedin,
    Facebook,
    Info,
    Send,
    Loader,
    X // Import X for removing emails if we implement chips
} from "lucide-react";
import { z } from "zod";

const emailSchema = z.string().email();

interface ReferUserDialogProps extends React.ComponentPropsWithoutRef<typeof Dialog> { }

const ReferUserDialog = ({ ...props }: ReferUserDialogProps) => {
    const { user } = useUser();
    const [emails, setEmails] = React.useState<string[]>([]);
    const [inputValue, setInputValue] = React.useState("");
    const [isSending, setIsSending] = React.useState(false);
    const { copyToClipboard } = useClipboard();

    // Construct referral link: prefer user.referral_link, fallback to constructing it
    const referralLink = user?.referral_link ||
        `${process.env.APP_FRONTEND_URL || "https://app-dev.legalassistant.au"}/auth/register?refer_by=${user?.email || ""}`;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const isValidEmail = (email: string) => {
        return emailSchema.safeParse(email).success;
    };

    const addEmail = (emailInput: string) => {
        const trimmedEmail = emailInput.trim();
        if (!trimmedEmail) return;

        if (!isValidEmail(trimmedEmail)) {
            toast.error(`Invalid email: ${trimmedEmail}`);
            return;
        }

        if (emails.includes(trimmedEmail)) {
            toast.error(`Email already added: ${trimmedEmail}`);
            return;
        }

        if (emails.length >= 5) {
            toast.error("You can only send 5 invitations at a time.");
            return;
        }

        setEmails([...emails, trimmedEmail]);
        setInputValue("");
    };

    // Auto-add email chip when a valid email is entered and user pauses typing
    React.useEffect(() => {
        const timer = setTimeout(() => {
            if (inputValue && isValidEmail(inputValue)) {
                addEmail(inputValue);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [inputValue, emails]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (["Enter", ",", " "].includes(e.key)) {
            e.preventDefault();
            addEmail(inputValue);
        } else if (e.key === "Backspace" && inputValue === "" && emails.length > 0) {
            e.preventDefault();
            const newEmails = [...emails];
            newEmails.pop();
            setEmails(newEmails);
        }
    };

    const handleRemoveEmail = (emailToRemove: string) => {
        setEmails(emails.filter((email) => email !== emailToRemove));
    };

    const handleSendInvitations = async () => {
        if (emails.length === 0) {
            // If there is text in input, try to add it first
            if (inputValue.trim()) {
                addEmail(inputValue.trim());
                // Need to return here and let user click again or recursively call, 
                // but for simplicity let's just add it and if valid proceed? 
                // Better to just add it and let them click again or check active state.
                // Let's just check if we have emails after potential add.
                // Re-evaluating: State update is async. 
                // So if input has value, warn user to press enter or handle it.
                // Actually, for UX, if they typed an email and clicked send, we should probably include it.
                // But validation is needed.
                // Let's safe path: if emails is empty and input is empty -> error.
                if (!inputValue.trim()) {
                    toast.error("Please add at least one email address.");
                    return;
                }
                // If input has value, we can't easily sync wait for state. 
                // Let's just tell them to add it.
                toast.error("Please press Enter to add the email.");
                return;
            }
            toast.error("Please add at least one email address.");
            return;
        }

        setIsSending(true);
        try {
            await referUser({ referEmails: emails });
            toast.success("Invitations sent successfully!");
            setEmails([]);
            setInputValue("");
            props.onOpenChange?.(false);
        } catch (error) {
            console.error(error);
            toast.error("Failed to send invitations. Please try again.");
        } finally {
            setIsSending(false);
        }
    };

    const handleCopyLink = () => {
        copyToClipboard(referralLink, "Referral link copied to clipboard!");
    };

    const openSocialShare = (platform: "twitter" | "linkedin" | "facebook") => {
        const text = "Check out AI Legal Assistant! Get $100 credit to trial it.";
        let url = "";
        switch (platform) {
            case "twitter":
                url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(text)}`;
                break;
            case "linkedin":
                url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`;
                break;
            case "facebook":
                url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`;
                break;
        }
        window.open(url, "_blank");
    };

    return (
        <Dialog {...props}>
            <DialogContent className="sm:max-w-[700px] p-0 gap-0 overflow-hidden">
                <div className="p-6 pb-2">
                    <DialogHeader className="mb-4">
                        <DialogTitle className="text-lg font-bold">Share the Love!</DialogTitle>
                        <DialogDescription className="text-sm text-black font-medium">
                            Earn up to $3,000 credit by sharing your experience with others.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 mb-6">
                        {/* Give $100 & Get $100 Card */}
                        <div className="flex items-start gap-4 p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-300 text-neutral-white shrink-0">
                                <CircleDollarSign size={16} />
                            </div>
                            <div>
                                <h4 className="font-semibold text-xs">Give $600 & Get $600</h4>
                                <p className="text-xs text-neutral-500">
                                    Give someone $100 credit to trial AI Legal Assistant and we'll give you $100 credit too. If they continue after the trial, we'll add an additional $500 credit to both accounts.
                                </p>
                            </div>
                        </div>
                    </div>

                    <Tabs defaultValue="email" className="w-full">
                        <TabsList className="grid w-full grid-cols-2 p-1.5 h-auto">
                            <TabsTrigger value="email" className="text-xs py-1.5">Email Invitations</TabsTrigger>
                            <TabsTrigger value="link" className="text-xs py-1.5">Share Link</TabsTrigger>
                        </TabsList>

                        <TabsContent value="email" className="mt-4 space-y-4">
                            <div className="text-center">
                                <h3 className="text-sm font-semibold mb-1">Send Email Invitations</h3>
                                <p className="text-xs text-neutral-400">
                                    Input an email then <span className="font-bold text-neutral-200">Press ENTER</span> or <span className="font-bold text-neutral-200">Comma</span>. You can add up to 5 email addresses at once.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Field>
                                    <FieldContent>
                                        <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px] focus-within:ring-1 focus-within:ring-ring bg-white">
                                            {emails.map((email) => (
                                                <div key={email} className="bg-neutral-700 text-neutral-100 text-xs px-3 py-1 rounded-full flex items-center gap-1">
                                                    {email}
                                                    <button onClick={() => handleRemoveEmail(email)} className="hover:text-danger-200">
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                            <Input
                                                className="flex-1 border-none focus-visible:ring-0 outline-none text-xs min-w-[200px] bg-transparent h-auto p-0 shadow-none"
                                                placeholder={emails.length === 0 ? "Enter Email Addresses and press Enter or Comma" : ""}
                                                value={inputValue}
                                                onChange={handleInputChange}
                                                onKeyDown={handleKeyDown}
                                                disabled={emails.length >= 5}
                                            />
                                        </div>
                                    </FieldContent>
                                </Field>
                                {emails.length === 0 && (
                                    <p className="text-xs text-danger-200">Please add at least one email address.</p>
                                )}
                            </div>

                            <Alert variant="info" className="p-3">
                                <Info className="h-4 w-4" />
                                <AlertDescription className="text-xs ml-2">
                                    <span className="font-bold">Note:</span> You cannot refer team members or yourself. By participating in this program, you agree to our <a href="#" className="underline">affiliate terms and conditions</a>.
                                </AlertDescription>
                            </Alert>

                            <div className="flex items-center justify-between mt-4">
                                <span className="text-xs text-neutral-400">{emails.length}/5 email addresses added</span>
                                <Button
                                    onClick={handleSendInvitations}
                                    className="bg-primary-300 hover:bg-primary-300-pressed text-neutral-white h-8 text-xs"
                                    disabled={isSending}
                                >
                                    {isSending ? (
                                        <>
                                            <Loader className="mr-2 h-3 w-3 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="mr-2 h-3 w-3" />
                                            Send Invitation
                                        </>
                                    )}
                                </Button>
                            </div>
                        </TabsContent>

                        <TabsContent value="link" className="mt-4 space-y-6">
                            <div className="text-center">
                                <h3 className="text-sm font-semibold mb-1">Share Your Referral Link</h3>
                                <p className="text-xs text-neutral-400">
                                    Copy your unique referral link and share it anywhere!
                                </p>
                            </div>

                            <div className="space-y-1">
                                <p className="text-xs text-neutral-400">Your Referral Link</p>
                                <div className="flex gap-2">
                                    <Input readOnly value={referralLink} className="bg-neutral-700 text-neutral-200 font-mono text-xs h-8" />
                                    <Button onClick={handleCopyLink} className="bg-primary-300 hover:bg-primary-300-pressed text-neutral-white whitespace-nowrap h-8 text-xs">
                                        <Copy className="mr-2 h-3 w-3" />
                                        Copy
                                    </Button>
                                </div>
                            </div>

                            <div className="flex flex-col items-center gap-3">
                                <span className="text-xs font-semibold">Share on Social Media</span>
                                <div className="flex gap-2">
                                    <Button size="sm" onClick={() => openSocialShare("twitter")} className="bg-[#1DA1F2] hover:bg-[#1a91da] text-white border-none h-8 text-xs">
                                        <Twitter className="mr-2 h-3 w-3" />
                                        Twitter
                                    </Button>
                                    <Button size="sm" onClick={() => openSocialShare("linkedin")} className="bg-[#0077B5] hover:bg-[#006093] text-white border-none h-8 text-xs">
                                        <Linkedin className="mr-2 h-3 w-3" />
                                        LinkedIn
                                    </Button>
                                    <Button size="sm" onClick={() => openSocialShare("facebook")} className="bg-[#4267B2] hover:bg-[#365899] text-white border-none h-8 text-xs">
                                        <Facebook className="mr-2 h-3 w-3" />
                                        Facebook
                                    </Button>
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ReferUserDialog;
